import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Menu, MenuItem, Backdrop } from '@mui/material';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import download from '../../images/downloads.png';
import filter from '../../images/filter.png';
import ApplicationIdWithStatusDot from '../applicationIdCell/ApplicationIdWithStatusDot';
import './basicTable.scss';
import TablePagination from './tablePagination/tablePagination';
import AppLoader from '../AppLoader/AppLoader';
function renderTableCell(column, row) {
    if (column.renderCell) {
        return column.renderCell(row);
    }
    if (column.field === 'applicationId') {
        return (
            <ApplicationIdWithStatusDot
                applicationId={row.applicationId}
                needsAction={Boolean(row.releventApplicationFlage)}
                noTruncate={Boolean(column.showFullApplicationId)}
            />
        );
    }
    return row[column.field];
}

function normalizeColor(value) {
    return String(value || '').toLowerCase().replace(/\s/g, '');
}

function resolveTableHoverAccentClass(sx, columns) {
    const headerBg =
        sx?.headerStyle?.backgroundColor ||
        columns?.[0]?.headerStyle?.backgroundColor ||
        '';
    const color = normalizeColor(headerBg);

    if (color.includes('9d192f') || color.includes('c41e3a') || color.includes('b91c1c')) {
        return 'table-container--accent-red';
    }

    if (
        color.includes('ecedef') ||
        color.includes('f2f4f6') ||
        color.includes('e5e7eb') ||
        color.includes('f3f4f6')
    ) {
        return 'table-container--accent-light';
    }

    return 'table-container--accent-blue';
}

function getColStyle(column) {
    const headerStyle = column.headerStyle || {};
    const rowStyle = column.rowCellStyle || {};
    const width = headerStyle.width || rowStyle.width;
    const minWidth = headerStyle.minWidth || rowStyle.minWidth;
    const maxWidth = headerStyle.maxWidth || rowStyle.maxWidth;

    if (column.field === "srNo") {
        return {
            width: width || "72px",
            minWidth: minWidth || width || "72px",
            maxWidth: maxWidth || width || "72px",
        };
    }

    const style = {};
    if (width && width !== "1000px") style.width = width;
    if (minWidth) style.minWidth = minWidth;
    if (maxWidth) style.maxWidth = maxWidth;
    if (!style.width && !style.minWidth) {
        style.minWidth = "80px";
    }
    return Object.keys(style).length ? style : undefined;
}

function getHeaderThStyle(column) {
    const style = { ...(column.headerStyle || {}) };
    if (style.width === "1000px") {
        delete style.width;
    }
    return { ...style, ...(getColStyle(column) || {}) };
}

function getEmptyHeaderThStyle(column) {
    const style = { ...(column.headerStyle || {}) };
    if (style.width === "1000px") {
        delete style.width;
    }
    delete style.minWidth;
    delete style.maxWidth;
    return style;
}

function getCellClassName(column) {
    if (column.field === 'downloads') return 'basic-table-docs-cell';
    if (column.field === 'applicationId' && !column.renderCell) {
        return 'basic-table-app-id-cell';
    }
    if (column.field === 'srNo') {
        return 'basic-table-srno-cell';
    }
    return undefined;
}

function getHeaderClassName(column) {
    if (column.headerName === "Docs.") {
        return "basic-table-docs-header";
    }
    if (column.field === "srNo") {
        return "basic-table-srno-header";
    }
    return undefined;
}

function getCellOverflow(column) {
    if (
        column.field === 'downloads' ||
        column.field === 'applicationId' ||
        column.showFullText ||
        column.showFullApplicationId
    ) {
        return 'visible';
    }
    return 'hidden';
}

function getEmptyRowCellStyle(rowHeight) {
    const minHeight =
        rowHeight && rowHeight > 0
            ? Math.max(26, Math.round(rowHeight * 1.25))
            : 26;
    const fontSize =
        rowHeight && rowHeight <= 20 ? "0.875rem" : "0.95rem";

    return {
        textAlign: "center",
        padding: "7px 8px",
        fontSize,
        fontWeight: 600,
        lineHeight: 1.35,
        // color + background come from CSS (light/dark theme)
        minHeight,
        height: minHeight,
        verticalAlign: "middle",
        whiteSpace: "normal",
    };
}

function getEmptyColStyle(columns, column) {
    const columnCount = columns?.length || 1;
    const equalWidth = `${100 / columnCount}%`;

    if (column.field === "srNo") {
        return { width: equalWidth, minWidth: 0 };
    }

    return { width: equalWidth, minWidth: 0 };
}

function renderEmptyDataRow(columns, rowHeight) {
    const emptyStyle = getEmptyRowCellStyle(rowHeight);
    const columnCount = columns?.length || 1;

    return (
        <tr
            className="basic-table-empty-row"
            style={{ height: emptyStyle.height }}
        >
            <td colSpan={columnCount} style={emptyStyle}>
                No data found.
            </td>
        </tr>
    );
}

function renderDashboardPadRows(count, columns, rowHeight) {
    if (!count || count <= 0) return null;
    return Array.from({ length: count }, (_, index) => (
        <tr
            key={`dashboard-pad-${index}`}
            className="basic-table-pad-row"
            style={{ height: rowHeight }}
            aria-hidden="true"
        >
            <td colSpan={columns?.length || 1} />
        </tr>
    ));
}

function getSkeletonBarMeta(column, rowIndex) {
    const field = String(column?.field || '').toLowerCase();
    const seed = field.length * 11 + rowIndex * 17;

    if (field.includes('sr') || field === 'days') {
        return { width: `${34 + (seed % 10)}%`, variant: 'chip', lines: 1 };
    }
    if (field.includes('applicationid') || field.includes('date') || field.includes('load')) {
        return { width: `${60 + (seed % 16)}%`, variant: 'mid', lines: 1 };
    }
    if (field.includes('name') || field.includes('applicant')) {
        return { width: `${74 + (seed % 16)}%`, variant: 'wide', lines: 1 };
    }
    if (field.includes('description') || field.includes('address') || field.includes('step')) {
        return {
            width: `${78 + (seed % 14)}%`,
            variant: 'wide',
            lines: 2,
            secondWidth: `${48 + (seed % 22)}%`,
        };
    }
    return { width: `${55 + (seed % 28)}%`, variant: 'mid', lines: 1 };
}

function renderSkeletonRows(count, columns, rowHeight) {
    if (!count || count <= 0 || !columns?.length) return null;
    const skeletonHeight = Math.max(Number(rowHeight) || 0, 44);
    return Array.from({ length: count }, (_, rowIndex) => (
        <tr
            key={`skeleton-row-${rowIndex}`}
            className="basic-table-skeleton-row"
            style={{
                height: skeletonHeight,
                '--sk-delay': `${rowIndex * 0.055}s`,
            }}
            aria-hidden="true"
        >
            {columns.map((column, colIndex) => {
                const meta = getSkeletonBarMeta(column, rowIndex);
                const shineDelay = `${rowIndex * 0.08 + colIndex * 0.05}s`;
                return (
                    <td
                        key={`${column.field}-${rowIndex}`}
                        className="basic-table-skeleton-cell"
                        style={{
                            textAlign: column.align || 'left',
                            height: skeletonHeight,
                            maxHeight: 'none',
                            overflow: 'visible',
                        }}
                    >
                        <div className="basic-table-skeleton-stack">
                            <span
                                className={`basic-table-skeleton-bar basic-table-skeleton-bar--${meta.variant}`}
                                style={{
                                    width: meta.width,
                                    '--sk-shine-delay': shineDelay,
                                }}
                            />
                            {meta.lines === 2 && (
                                <span
                                    className="basic-table-skeleton-bar basic-table-skeleton-bar--sub"
                                    style={{
                                        width: meta.secondWidth,
                                        '--sk-shine-delay': `calc(${shineDelay} + 0.12s)`,
                                    }}
                                />
                            )}
                        </div>
                    </td>
                );
            })}
        </tr>
    ));
}

/**
 * A customizable table component with sorting, filtering, pagination, and row interactions.
 *
 * @component
 * @memberof Components
 * @param {Object} props - Component properties
 * @param {Array<Object>} props.columns - Column configuration array
 * @param {Array<Object>} props.data - Table data
 * @param {Object} [props.sx] - Styles for the table
 * @param {number} [props.rowHeight] - Height of each row
 * @param {Function} [props.handleRowClick] - Callback function for row click event
 * @param {number} [props.pageSize] - Number of rows per page
 * @param {Array<number>} [props.rowsPerPageOptions] - Available page size options
 * @param {Function} [props.getRowStyle] - Function to determine row styles
 * @param {Function} [props.firstPage] - Function to navigate to the first page
 * @param {Function} [props.nextPage] - Function to navigate to the next page
 * @param {Function} [props.previousPage] - Function to navigate to the previous page
 * @param {Function} [props.lastPage] - Function to navigate to the last page
 * @param {number} [props.totalPages] - Total number of pages
 * @param {number} [props.totalRecords] - Total number of records
 * @param {number} [props.currentPage] - Current page number
 * @param {Function} [props.handleFilterDateFrom] - Function to filter by start date
 * @param {Function} [props.handleFilterDateTo] - Function to filter by end date
 * @param {Function} [props.handleFilterLoadFrom] - Function to filter by minimum load
 * @param {Function} [props.handleFilterLoadTo] - Function to filter by maximum load
 * @param {Function} [props.handleFilterProgressId] - Function to filter by progress ID
 * @param {Function} [props.handleFilterObjectionStatus] - Function to filter by objection status
 * @param {Function} [props.resetProgressIdFilter] - Function to reset progress ID filter
 * @param {Function} [props.resetObjectionStatusFilter] - Function to reset objection status filter
 * @param {Function} [props.resetDateFilter] - Function to reset date filter
 * @param {Function} [props.resetLoadFilter] - Function to reset load filter
 * @param {boolean} [props.deferRangeFilterApply=false] - Keep date/load edits local until Apply
 * @param {boolean} [props.showFooter=true] - Whether to show pagination footer
 * @param {boolean} [props.showResolvedButton=false] - Whether to show the "Resolve" button
 * @param {Function} [props.setRowData] - Function to update row data
 *
 * @returns {JSX.Element} The rendered table component
 */
const BasicTable = ({ columns, data, sx, rowHeight, handleRowClick, pageSize, rowsPerPageOptions, getRowStyle, rowsPageOption, firstPage, nextPage, previousPage, lastPage, totalPages, totalRecords, currentPage, handleFilterDateFrom, handleFilterDateTo, handleFilterLoadFrom, handleFilterLoadTo, handleFilterProgressId, handleFilterObjectionStatus, resetProgressIdFilter, resetObjectionStatusFilter, resetDateFilter, resetLoadFilter, deferRangeFilterApply = false, showFooter, showResolvedButton, setRowData, isLoading, fillLoadingHeight = false, showLoader = true, loadingVariant = 'skeleton' }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pageSize);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openColumnMenu, setOpenColumnMenu] = useState(null);
    const [filters, setFilters] = useState({});
    const [dateFilters, setDateFilters] = useState({});
    const [loadFilters, setLoadFilters] = useState({});
    const dateFiltersRef = useRef(dateFilters);
    const loadFiltersRef = useRef(loadFilters);
    dateFiltersRef.current = dateFilters;
    loadFiltersRef.current = loadFilters;
    const containerRef = useRef(null);
    const theadRef = useRef(null);
    const isEmptyState = !isLoading && data && data.length === 0;
    const useSkeletonLoading = isLoading && loadingVariant === 'skeleton';
    const showSpinnerLoader = isLoading && showLoader && !useSkeletonLoading;
    const hoverAccentClass = useMemo(
        () => resolveTableHoverAccentClass(sx, columns),
        [sx, columns]
    );

    useLayoutEffect(() => {
        const container = containerRef.current;
        const thead = theadRef.current;
        if (!container || !thead) return;

        const syncHeaderHeight = () => {
            const headerHeight = Math.ceil(thead.getBoundingClientRect().height);
            container.style.setProperty(
                '--basic-table-header-height',
                `${headerHeight}px`
            );
        };

        syncHeaderHeight();
        if (!isLoading) return undefined;

        const observer = new ResizeObserver(syncHeaderHeight);
        observer.observe(thead);
        window.addEventListener('resize', syncHeaderHeight);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', syncHeaderHeight);
        };
    }, [isLoading, columns]);

    useEffect(() => {
        setPage(0); 
    }, [filters, dateFilters, loadFilters]);

    // Removed redundant useEffect - handlers are already called directly in
    // handleFilter, handleDateFilter, handleLoadFilter, and clearFilter functions

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseMenu = () => {
        // Prevent focused MenuItem from remaining inside aria-hidden popover on close.
        if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur();
        }
        setAnchorEl(null);
        setOpenColumnMenu(null); // Close currently open column menu
    };

    const handleFilter = (column, value, index) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [column.field]: value,
        }));
        if (handleFilterProgressId) handleFilterProgressId(index);
        handleCloseMenu();
    };

    const clearFilter = (column) => {
        setFilters(prevFilters => {
            const newFilters = { ...prevFilters };
            delete newFilters[column.field];
            return newFilters;
        });
        setDateFilters(prevFilters => {
            const newFilters = { ...prevFilters };
            delete newFilters[column.field];
            return newFilters;
        });
        setLoadFilters(prevFilters => {
            const newFilters = { ...prevFilters };
            delete newFilters[column.field];
            return newFilters;
        });
        switch (column.field) {
            case 'applicationProgressId':
            case 'applicationStep':
            case 'applicationStatusId':
                if (resetProgressIdFilter) resetProgressIdFilter()
                if (resetObjectionStatusFilter) resetObjectionStatusFilter()
                break;
            case 'creationDate':
                if (resetDateFilter) resetDateFilter()
                break;
            case 'appliedLoad':
                if (resetLoadFilter) resetLoadFilter()
                break;
        }
        handleCloseMenu();
    };

    const handleDateFilter = (column, field, value) => {
        setDateFilters(prevFilters => ({
            ...prevFilters,
            [column.field]: {
                ...prevFilters[column.field],
                [field]: value,
            }
        }));
        if (deferRangeFilterApply) return;
        if (field == 'dateFrom') {
            handleFilterDateFrom(value)
        } else if (field === 'dateTo') {
            handleFilterDateTo(value)
        }
    };

    const handleLoadFilter = (column, field, value) => {
        setLoadFilters(prevFilters => ({
            ...prevFilters,
            [column.field]: {
                ...prevFilters[column.field],
                [field]: value,
            }
        }));
        if (deferRangeFilterApply) return;
        if (field == 'loadFrom') {
            handleFilterLoadFrom(value)
        } else if (field === 'loadTo') {
            handleFilterLoadTo(value)
        }
    };

    const applyPendingRangeFilters = (column) => {
        if (column?.type === 'date') {
            const dates = dateFiltersRef.current[column.field] || {};
            if (dates.dateFrom) handleFilterDateFrom?.(dates.dateFrom);
            if (dates.dateTo) handleFilterDateTo?.(dates.dateTo);
        }
        if (column?.type === 'range') {
            const loads = loadFiltersRef.current[column.field] || {};
            if (loads.loadFrom) handleFilterLoadFrom?.(loads.loadFrom);
            if (loads.loadTo) handleFilterLoadTo?.(loads.loadTo);
        }
        handleCloseMenu();
    };

    const handleRowHover = index => {
        setHoveredRow(index);
    };

    const handleRowLeave = () => {
        setHoveredRow(null);
    };

    const handleClickMenu = (event, column) => {
        setAnchorEl(event.currentTarget);
        setOpenColumnMenu(column.field); // Set the currently open column menu
    };

    const handleButtonClick = (row) => {
        setRowData(row)
        // You can now access the row data here and perform any action you need.
    };

    return (
        <div
            ref={containerRef}
            className={`table-container ${hoverAccentClass}${
                handleRowClick ? " table-container--interactive" : ""
            }${
                isLoading ? " table-container--loading" : ""
            }${useSkeletonLoading ? " table-container--skeleton" : ""}${
                isEmptyState ? " table-container--empty" : ""
            }`}
        >
            {showSpinnerLoader && (
                <div className="basic-table-loading-panel" aria-busy="true">
                    <AppLoader />
                </div>
            )}
            <table
                style={{
                    ...sx,
                    ...(isEmptyState
                        ? { width: "100%", tableLayout: "fixed" }
                        : {}),
                }}
            >
                {columns?.length > 0 && (
                    <colgroup>
                        {columns.map((column) => (
                            <col
                                key={column.field}
                                style={
                                    isEmptyState
                                        ? getEmptyColStyle(columns, column)
                                        : getColStyle(column)
                                }
                            />
                        ))}
                    </colgroup>
                )}
                <thead ref={theadRef}>
                    <tr >
                        {columns?.map((column, index) => (
                            <th
                                key={column.field}
                                className={getHeaderClassName(column)}
                                style={isEmptyState ? getEmptyHeaderThStyle(column) : getHeaderThStyle(column)}
                            >
                                <div
                                    style={{
                                        ...column.headerCellStyle,
                                        justifyContent:
                                            column.headerName === "Docs."
                                                ? "center"
                                                : column.align,
                                    }}
                                >
                                    {column.headerName === "Docs." ? (
                                        <img
                                            src={download}
                                            alt="download"
                                            width={20}
                                            style={{ display: "block" }}
                                        />
                                    ) : (
                                        <p style={{ padding: 0, margin: 0 }}>
                                            {column.headerName}
                                        </p>
                                    )}
                                    <>
                                        {
                                            (column.filterable === true) &&
                                            <div className="basic-table-filter-controls">
                                                <img className='filter-icon' src={filter} alt='filter' onClick={(e) => handleClickMenu(e, column)} />
                                                {(filters[column.field] || dateFilters[column.field] || loadFilters[column.field]) && (
                                                    <IconButton
                                                        className="filter-reset-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            clearFilter(column);
                                                        }}
                                                        aria-label="Reset filter"
                                                        sx={{ padding: 0, margin: 0, backgroundColor: '#e11b3c', color: 'white' }}
                                                    >
                                                        <CloseIcon sx={{ fontSize: '12px' }} />
                                                    </IconButton>
                                                )}
                                            </div>
                                        }
                                        <Menu
                                            id={`column-menu-${column.field}`}
                                            anchorEl={anchorEl}
                                            open={openColumnMenu === column.field} // Open only if it's the currently open column menu
                                            onClose={handleCloseMenu}
                                            disableAutoFocus
                                            disableEnforceFocus
                                            sx={{
                                                '& .MuiPaper-root': {
                                                    boxShadow: '0px 2.5px 10px rgba(0, 0, 0, 0.25)', // Adjust the shadow appearance
                                                },
                                                minWidth: '200px',
                                                top: 10
                                            }}
                                        >
                                            {column.filterable === true && column.type === "singleSelect" &&
                                                column.valueOptions?.map((item, index) => (
                                                    <MenuItem
                                                        key={index}
                                                        value={index}
                                                        style={{ width: '100%' }}
                                                        onClick={() => handleFilter(column, item, index)}
                                                        selected={filters[column.field] === item}
                                                        sx={{
                                                            backgroundColor: filters[column.field] === item ? 'lightblue' : 'transparent',
                                                            '&:hover': {
                                                                backgroundColor: filters[column.field] === item ? 'lightblue' : 'lightgrey',
                                                            },
                                                        }}
                                                    >
                                                        {item}
                                                    </MenuItem>
                                                ))
                                            }
                                            {column.filterable === true && column.type === "date" && (
                                                <>
                                                    <MenuItem key="dateFrom" sx={{ display: 'flex', flexDirection: 'column', margin: 0, padding: '5px', alignItems: 'start' }}>
                                                        <label>Date from</label>
                                                        <input
                                                            type='date'
                                                            style={{ padding: 10, }}
                                                            onChange={(e) => handleDateFilter(column, 'dateFrom', e.target.value)}
                                                            value={dateFilters[column.field]?.dateFrom || ''}
                                                        />
                                                    </MenuItem>
                                                    <MenuItem key="dateTo" sx={{ display: 'flex', flexDirection: 'column', margin: 0, padding: '5px', alignItems: 'start' }}>
                                                        <label>Date To</label>
                                                        <input
                                                            type='date'
                                                            style={{ padding: 10, }}
                                                            onChange={(e) => handleDateFilter(column, 'dateTo', e.target.value)}
                                                            value={dateFilters[column.field]?.dateTo || ''}
                                                        />
                                                    </MenuItem>
                                                    {deferRangeFilterApply && (
                                                        <MenuItem
                                                            key="apply-date"
                                                            onClick={() => applyPendingRangeFilters(column)}
                                                            sx={{ justifyContent: 'center', fontWeight: 600 }}
                                                        >
                                                            Apply
                                                        </MenuItem>
                                                    )}
                                                </>
                                            )}
                                            {column.filterable === true && column.type === "range" && (
                                                <>
                                                    <MenuItem key="loadFrom" sx={{ display: 'flex', flexDirection: 'column', margin: 0, padding: '5px', alignItems: 'start' }}>
                                                        <label>Load from</label>
                                                        <input
                                                            type='number'
                                                            style={{ padding: 10, }}
                                                            onChange={(e) => handleLoadFilter(column, 'loadFrom', e.target.value)}
                                                            value={loadFilters[column.field]?.loadFrom || ''}
                                                        />
                                                    </MenuItem>
                                                    <MenuItem key="loadTo" sx={{ display: 'flex', flexDirection: 'column', margin: 0, padding: '5px', alignItems: 'start' }}>
                                                        <label>Load To</label>
                                                        <input
                                                            type='number'
                                                            style={{ padding: 10, }}
                                                            onChange={(e) => handleLoadFilter(column, 'loadTo', e.target.value)}
                                                            value={loadFilters[column.field]?.loadTo || ''}
                                                        />
                                                    </MenuItem>
                                                    {deferRangeFilterApply && (
                                                        <MenuItem
                                                            key="apply-load"
                                                            onClick={() => applyPendingRangeFilters(column)}
                                                            sx={{ justifyContent: 'center', fontWeight: 600 }}
                                                        >
                                                            Apply
                                                        </MenuItem>
                                                    )}
                                                </>
                                            )}
                                        </Menu>
                                    </>
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                {/* <tbody>
                    {
                        isLoading &&
                        <>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100vh',  // full screen height
                                width: "100vh",
                                backgroundColor:'yellow'
                            }}>
                                <AppLoader />
                            </div>
                             < Backdrop
                                    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }}
                                    open={isLoading}
                                  >
                                    <AppLoader />
                                  </Backdrop >
                        </>
                    }
                    
                    {data && data.length === 0 ? (
                        <tr>
                            <td
                                colSpan="15"
                                style={{
                                    textAlign: "center",
                                    padding: "10px",
                                    fontSize: "16px",
                                    color: "red",
                                    backgroundColor: '#fcebeb'
                                }}
                            >
                                No data found
                            </td>
                        </tr>
                    ) : (
                        data?.map((row, index) => (
                            <tr
                                key={index}
                                style={{
                                    ...sx.rowStyle,
                                    ...getRowStyle({ indexRelativeToCurrentPage: index }),
                                    ...(index === hoveredRow && { backgroundColor: 'white' }),
                                    height: rowHeight,
                                }}
                                onClick={() => handleRowClick && handleRowClick(row)}
                                onMouseEnter={() => handleRowHover(index)}
                                onMouseLeave={handleRowLeave}
                            >
                                {columns?.map((column) => (
                                    <td
                                        key={column.field}
                                        className={
                                            column.field === "downloads"
                                                ? "basic-table-docs-cell"
                                                : undefined
                                        }
                                        style={{
                                            textAlign: column.align,
                                            ...column.rowCellStyle,
                                            ...(rowHeight
                                                ? {
                                                      height: rowHeight,
                                                      maxHeight: rowHeight,
                                                      lineHeight: 1.15,
                                                      overflow:
                                                          column.field === "downloads"
                                                              ? "visible"
                                                              : "hidden",
                                                      boxSizing: "border-box",
                                                  }
                                                : {}),
                                        }}
                                    >
                                        {column.renderCell ? column.renderCell(row) : row[column.field]}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody> */}
                <tbody>
                    {isLoading ? (
                        useSkeletonLoading ? (
                            renderSkeletonRows(
                                Math.max(pageSize || 12, 10),
                                columns,
                                rowHeight
                            )
                        ) : fillLoadingHeight ? (
                            renderDashboardPadRows(
                                pageSize || 20,
                                columns,
                                rowHeight
                            )
                        ) : (
                            <tr aria-hidden="true">
                                <td
                                    colSpan={columns?.length || 1}
                                    style={{ height: 240, border: "none", padding: 0 }}
                                />
                            </tr>
                        )
                    ) : data && data.length === 0 ? (
                        <>
                            {renderEmptyDataRow(columns, rowHeight)}
                            {fillLoadingHeight &&
                                renderDashboardPadRows(
                                    Math.max(0, (pageSize || 20) - 1),
                                    columns,
                                    rowHeight
                                )}
                        </>
                    ) : (
                        <>
                        {data?.map((row, index) => (
                            <tr
                                key={index}
                                className={
                                  index === hoveredRow
                                    ? "basic-table-row is-hovered"
                                    : "basic-table-row"
                                }
                                style={{
                                    ...sx.rowStyle,
                                    ...getRowStyle({ indexRelativeToCurrentPage: index }),
                                    height: rowHeight,
                                }}
                                onClick={() => handleRowClick && handleRowClick(row)}
                                onMouseEnter={() => handleRowHover(index)}
                                onMouseLeave={handleRowLeave}
                            >
                                {columns?.map((column) => (
                                    <td
                                        key={column.field}
                                        className={getCellClassName(column)}
                                        style={{
                                            textAlign: column.align,
                                            ...column.rowCellStyle,
                                            ...(rowHeight
                                                ? {
                                                      height: rowHeight,
                                                      maxHeight: rowHeight,
                                                      lineHeight: 1.15,
                                                      overflow: getCellOverflow(column),
                                                      boxSizing: "border-box",
                                                  }
                                                : {}),
                                        }}
                                    >
                                        {renderTableCell(column, row)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {fillLoadingHeight &&
                            renderDashboardPadRows(
                                Math.max(0, (pageSize || 20) - (data?.length || 0)),
                                columns,
                                rowHeight
                            )}
                        </>
                    )}
                </tbody>


            </table>
            {
                (showFooter !== false) &&
                <TablePagination
                    rowsPerPageOptions={rowsPerPageOptions}
                    count={totalRecords}
                    rowsPerPage={rowsPerPage}
                    page={page ? page : 1}
                    currentPage={currentPage ? currentPage : 1}
                    totalPages={totalPages ? totalPages : 1}
                    totalRecords={totalRecords}
                    visibleRowCount={data?.length ?? 0}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                    rowsPageOption={rowsPageOption}
                    nextPage={nextPage}
                    previousPage={previousPage}
                    firstPage={firstPage}
                    lastPage={lastPage}
                />
            }
        </div>
    );
};

export default BasicTable;
